/** Private functions */
function __getScene(object) {
  if (!object) {
    throw new Error('object not found');
  }
  if (!(object instanceof Phaser.GameObjects.GameObject)) {
    throw new Error('object is not a valid Phaser Object');
  }
  if (!object.scene) {
    throw new Error("object doesn't have a valid scene");
  }

  return object.scene;
}

function __tween(object, props, duration = 1000, config = {}) {
  const scene = __getScene(object);

  let resolveFn;

  const promise = new Promise(resolve => {
    resolveFn = resolve;
  });

  const tween = scene.tweens.add({
    targets: object,
    duration,
    ease: config.ease ?? 'Linear',
    repeat: config.repeat ?? 0,
    yoyo: config.yoyo ?? false,
    delay: config.delay ?? 0,
    ...props,
    onComplete: () => {
      config.onComplete?.();
      resolveFn();
    },
  });

  promise.stop = () => {
    if (tween && tween.isPlaying()) {
      tween.stop();
    }
    resolveFn();
  };

  return promise;
}

/** Public functions */
function fade(object, alpha, duration = 1000, config = {}) {
  return new Promise(resolve => {
    const scene = __getScene(object);
    const delay = config.delay ?? 0;

    const alphaMap = {
      from: alpha?.from ?? object.alpha,
      to: alpha ?? alpha?.to ?? (object.alpha === 0 ? 1 : 0),
    };

    scene.time.delayedCall(delay, () => {
      scene.tweens.add({
        targets: object,
        alpha: alphaMap,
        ease: config.ease ?? 'Cubic.easeInOut',
        duration,
        repeat: config.repeat ?? 0,
        yoyo: config.yoyo ?? false,
        onComplete: () => {
          config.onComplete?.();
          resolve();
        },
      });
    });
  });
}

function move(object, position, duration = 1000, config = {}) {
  return new Promise(resolve => {
    const scene = __getScene(object);
    const delay = config.delay ?? 0;

    scene.time.delayedCall(delay, () => {
      scene.tweens.add({
        targets: object,
        x: position.x ?? object.x,
        y: position.y ?? object.y,
        ease: config.ease ?? 'Linear',
        duration,
        repeat: config.repeat ?? 0,
        yoyo: config.yoyo ?? false,
        onComplete: () => {
          config.onComplete?.();
          resolve();
        },
      });
    });
  });
}

function resize(object, scale, duration = 1000, config = {}) {
  return new Promise(resolve => {
    const scene = __getScene(object);
    const delay = config.delay ?? 0;

    let scaleX, scaleY;
    if (typeof scale !== 'number') {
      scaleX = scale.x ?? 1;
      scaleY = scale.y ?? 1;
    } else scaleX = scaleY = scale;

    scene.time.delayedCall(delay, () => {
      scene.tweens.add({
        targets: object,
        scaleX: scaleX,
        scaleY: scaleY,
        ease: config.ease ?? 'Linear',
        duration,
        repeat: config.repeat ?? 0,
        yoyo: config.yoyo ?? false,
        onComplete: () => {
          config.onComplete?.();
          resolve();
        },
      });
    });
  });
}

function switchTexture(object, texture, duration = 1000, config = {}) {
  return new Promise(resolve => {
    const scene = __getScene(object);
    const delay = config.delay ?? 0;

    if (!texture) {
      throw new Error('texture not set or found');
    }

    const fadeOut = config.transition === 'fadeOut';
    const fadeOutIn = config.transition === 'fadeOutIn';
    const crossFade = config.transition === 'crossFade';

    if (fadeOutIn) duration = duration / 2;

    scene.time.delayedCall(delay, () => {
      const tempObj = scene.add
        .sprite(object.x, object.y, fadeOut ? object.texture : texture)
        .setOrigin(object.originX, object.originY)
        .setScale(object.scaleX, object.scaleY)
        .setRotation(object.rotation)
        .setAlpha(fadeOut ? 1 : 0)
        .setDepth(object.depth + 1);

      if (fadeOut) object.setTexture(texture);

      scene.tweens.add({
        targets: tempObj,
        alpha: fadeOut ? 0 : fadeOutIn ? 1 : object.alpha,
        ease: config.ease ?? 'Linear',
        duration,
        delay: fadeOutIn ? duration : 0,
        repeat: config.repeat ?? 0,
        yoyo: config.yoyo ?? false,
        onComplete: () => {
          object.setTexture(texture);
          if (fadeOutIn) object.setAlpha(1);

          tempObj.destroy();

          config.onComplete?.();
          resolve();
        },
      });

      if (fadeOutIn || crossFade) {
        scene.tweens.add({
          targets: object,
          alpha: 0,
          ease: config.ease ?? 'Linear',
          duration,
          repeat: config.repeat ?? 0,
          yoyo: config.yoyo ?? false,
          onComplete: () => {
            if (crossFade) object.setAlpha(1);
          },
        });
      }
    });
  });
}

function blink(object, interval = 300, duration = 1000, config = {}) {
  const infinite = config.infinite ?? false;

  return __tween(object, { alpha: 0 }, interval, {
    ...config,
    duration,
    repeat: infinite ? -1 : (config.repeat ?? 3),
    yoyo: true,
    onComplete: () => {
      object.setAlpha(1);
      config.onComplete?.();
    },
  });
}

function shake(object, intensity = 3, duration = 300, config = {}) {
  const scene = __getScene(object);

  const axis = config.axis ?? 'both';
  const startX = object.x;
  const startY = object.y;

  let resolveFn;

  const promise = new Promise(resolve => {
    resolveFn = resolve;
  });

  const tween = scene.tweens.addCounter({
    from: 0,
    to: 1,
    duration,
    ease: config.ease ?? 'Linear',
    delay: config.delay ?? 0,
    onUpdate: () => {
      const offsetX = axis === 'y' ? 0 : Phaser.Math.Between(-intensity, intensity);

      const offsetY = axis === 'x' ? 0 : Phaser.Math.Between(-intensity, intensity);

      object.setPosition(startX + offsetX, startY + offsetY);
    },
    onComplete: () => {
      object.setPosition(startX, startY);
      config.onComplete?.();
      resolveFn();
    },
  });

  promise.stop = () => {
    tween.stop();
    object.setPosition(startX, startY);
    resolveFn();
  };

  return promise;
}

export const GAME_FX = {
  fade,
  move,
  resize,
  switchTexture,
  blink,
  shake,
};
